import React, { useEffect, useState, useMemo } from 'react';
import { Form, Spinner, Modal, Button } from 'react-bootstrap';
import { FaHistory, FaSearch, FaTimes, FaArchive } from 'react-icons/fa';
import { fetchArchivedForms, restoreArchivedForm } from 'api/formApi';
import { toast } from 'react-toastify';

const ArchivedForms = () => {
    const [archivedList, setArchivedList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [restoreInProgress, setRestoreInProgress] = useState(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [selectedForm, setSelectedForm] = useState(null);

    const fetchList = async () => {
        setLoading(true);
        try {
            const response = await fetchArchivedForms();
            if (response && response.status === 200) {
                setArchivedList(response.data?.data || []);
            } else {
                toast.error(response?.message || 'Failed to fetch archived forms');
            }
        } catch (error) {
            toast.error(error.message || 'Error loading archived forms');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const prettifyFormName = (name) => {
        if (!name) return 'N/A';
        return name
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleRestoreClick = (form) => {
        setSelectedForm(form);
        setShowRestoreModal(true);
    };

    const handleConfirmRestore = async () => {
        if (!selectedForm) return;
        setRestoreInProgress(selectedForm._id);
        setShowRestoreModal(false);
        try {
            const response = await restoreArchivedForm(selectedForm._id);
            if (response && response.status === 200) {
                toast.success('Form successfully restored to its original place!');
                fetchList(); // Refresh list
            } else {
                toast.error(response?.message || 'Failed to restore form');
            }
        } catch (error) {
            toast.error(error.message || 'Error occurred while restoring form');
        } finally {
            setRestoreInProgress(null);
            setSelectedForm(null);
        }
    };

    const filteredList = useMemo(() => {
        return (archivedList || []).filter((item) => {
            const formNameMatch = prettifyFormName(item.formName).toLowerCase().includes(search.toLowerCase());
            const stationMatch = (item.data?.stationId || '').toLowerCase().includes(search.toLowerCase());
            const deletedByMatch = (item.deletedBy || '').toLowerCase().includes(search.toLowerCase());
            return formNameMatch || stationMatch || deletedByMatch;
        });
    }, [archivedList, search]);

    const totalArchived = archivedList.length;

    const statCards = [
        { label: 'Archived Items', value: totalArchived, icon: <FaArchive size={20} />, gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' },
        { label: 'Restore Available', value: 'Instant', icon: <FaHistory size={20} />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }
    ];

    return (
        <div className="page-body checklist-container">
            <div className="container-xl">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="page-title m-0" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            Archived Forms
                        </h2>
                        <p className="text-muted small mb-0 mt-1">Review and restore recently deleted forms back to their stations</p>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="row g-3 mb-4">
                    {statCards.map((card) => (
                        <div className="col-12 col-md-6" key={card.label}>
                            <div style={{
                                background: card.gradient,
                                borderRadius: '16px',
                                padding: '20px 22px',
                                color: '#fff',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                transition: 'transform 0.2s ease',
                                cursor: 'default',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span style={{ opacity: 0.85, fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</span>
                                    <span style={{ opacity: 0.7 }}>{card.icon}</span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, lineHeight: 1 }}>{card.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Search */}
                <div className="premium-filter-section mb-4">
                    <div className="filter-group search-wrapper expanded" style={{ maxWidth: '100%' }}>
                        <label htmlFor="archive-search">Search Archived Forms</label>
                        <div className="position-relative">
                            <Form.Control
                                id="archive-search"
                                type="text"
                                placeholder="Search by Form Name, Station ID, or Deleted By..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>
                    {search && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button id="clear-search-btn" className="btn-clear" onClick={() => setSearch('')} title="Clear Search">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-3">Loading archive...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Form Type</th>
                                    <th>Station / ID</th>
                                    <th>Version</th>
                                    <th>Deleted By</th>
                                    <th>Deleted At</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((item) => (
                                    <tr key={item._id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                                    display: 'flex', alignItems: 'center', justifycontent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <FaArchive color="#fff" size={16} style={{ margin: 'auto' }} />
                                                </div>
                                                <div>
                                                    <span className="fw-bold" style={{ color: '#1e293b', fontSize: '14px' }}>
                                                        {prettifyFormName(item.formName)}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="fw-bold text-dark">
                                                {item.data?.stationId || item.data?.chargerId || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{
                                                background: '#f1f5f9', borderRadius: '8px', padding: '4px 12px',
                                                fontWeight: 700, fontSize: '13px', color: '#334155'
                                            }}>
                                                V{item.data?.version || 1}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-muted" style={{ fontSize: '13px' }}>
                                                {item.deletedBy || 'system'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-muted" style={{ fontSize: '13px' }}>
                                                {new Date(item.deletedAt).toLocaleString()}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="btn-premium"
                                                title="Restore Form"
                                                onClick={() => handleRestoreClick(item)}
                                                disabled={restoreInProgress === item._id}
                                                style={{
                                                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '6px 16px',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {restoreInProgress === item._id ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    <>
                                                        <FaHistory /> Restore
                                                    </>
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredList.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FaArchive size={48} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                <p className="text-muted fw-bold mb-1">No archived forms found</p>
                                                <p className="text-muted small">Everything is clean!</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Restore Confirmation Modal */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Restore</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div>
                            Are you sure you want to restore the form{' '}
                            <strong>{prettifyFormName(selectedForm?.formName)}</strong> for{' '}
                            <strong>{selectedForm?.data?.stationId || selectedForm?.data?.chargerId || 'N/A'}</strong> back to its original location?
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleConfirmRestore}>
                            Yes, Restore Form
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ArchivedForms;
